import * as React from "react"
import { createPortal } from "react-dom"
import { Link, useLocation } from "react-router-dom"
import { cn } from "@/lib/utils"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { resolveShellCrumb } from "./shellControl"

/**
 * ShellHeader — the 52px bar at the top of the white content panel
 * (White signal v2; Paper → App → "App v3 · … · LP-aligned").
 *
 * Left: "Group › Page" breadcrumb derived from the route, matching the sidebar
 * IA (Observe / Optimize / Agent & data). Right: whatever the layout passes as
 * `children` (the dashboard filter controls), plus anything a page portals in
 * through <ShellHeaderActions> (e.g. the view's one primary CTA). A page can
 * also extend the breadcrumb through <ShellHeaderCrumb> (e.g. the Heatmaps page
 * selector reads "Observe › Heatmaps › /pricing").
 */

export function ShellBreadcrumb({ className }: { className?: string }) {
  const { pathname, search } = useLocation()
  const crumb = resolveShellCrumb(pathname)
  if (!crumb) return null
  return (
    <nav aria-label="Breadcrumb" className={cn("shrink-0", className)}>
      <ol className="flex items-center gap-2 text-[13px] font-medium leading-4">
        {crumb.group && (
          <>
            <li className="text-foreground/60">{crumb.group}</li>
            <li aria-hidden className="text-foreground/30">›</li>
          </>
        )}
        {crumb.href ? (
          <li>
            <Link
              to={{ pathname: crumb.href, search }}
              className="rounded text-foreground/60 underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              {crumb.page}
            </Link>
          </li>
        ) : (
          <li aria-current="page" className="text-foreground">
            {crumb.page}
          </li>
        )}
      </ol>
    </nav>
  )
}

// ---------------------------------------------------------------------------
// Actions slot — pages can lift their primary CTA into the header bar.
// ---------------------------------------------------------------------------

const ShellActionsSlotContext = React.createContext<HTMLElement | null | undefined>(undefined)
const ShellSlotSetterContext = React.createContext<((el: HTMLElement | null) => void) | null>(null)
const ShellCrumbSlotContext = React.createContext<HTMLElement | null | undefined>(undefined)
const ShellCrumbSetterContext = React.createContext<((el: HTMLElement | null) => void) | null>(null)

/** True when rendered inside a shell that has mounted its header slots. */
export function useInShellHeader(): boolean {
  return React.useContext(ShellActionsSlotContext) !== undefined
}

/**
 * Render children into the shell header's right-hand actions slot. Outside a
 * shell (no slot mounted) the children render inline, so pages stay safe in
 * share mode, tests, or any layout without the header.
 */
export function ShellHeaderActions({ children }: { children: React.ReactNode }) {
  const slot = React.useContext(ShellActionsSlotContext)
  if (slot === undefined) return <>{children}</>
  if (slot === null) return null
  return createPortal(children, slot)
}

/**
 * Render children right after the breadcrumb, behind a "›" separator. Outside
 * a shell the children render inline.
 */
export function ShellHeaderCrumb({ children }: { children: React.ReactNode }) {
  const slot = React.useContext(ShellCrumbSlotContext)
  if (slot === undefined) return <>{children}</>
  if (slot === null) return null
  return createPortal(
    <>
      <span aria-hidden className="text-[13px] font-medium leading-4 text-foreground/30">›</span>
      {children}
    </>,
    slot,
  )
}

export function ShellHeaderProvider({ children }: { children: React.ReactNode }) {
  const [slot, setSlot] = React.useState<HTMLElement | null>(null)
  const [crumbSlot, setCrumbSlot] = React.useState<HTMLElement | null>(null)
  return (
    <ShellActionsSlotContext.Provider value={slot}>
      <ShellSlotSetterContext.Provider value={setSlot}>
        <ShellCrumbSlotContext.Provider value={crumbSlot}>
          <ShellCrumbSetterContext.Provider value={setCrumbSlot}>{children}</ShellCrumbSetterContext.Provider>
        </ShellCrumbSlotContext.Provider>
      </ShellSlotSetterContext.Provider>
    </ShellActionsSlotContext.Provider>
  )
}

interface ShellHeaderProps {
  /** Right-hand controls owned by the layout (e.g. the dashboard filter bar). */
  children?: React.ReactNode
  /** Optional content rendered under the 52px bar (e.g. active filter chips). */
  below?: React.ReactNode
  className?: string
}

export function ShellHeader({ children, below, className }: ShellHeaderProps) {
  const setSlot = React.useContext(ShellSlotSetterContext)
  const setCrumbSlot = React.useContext(ShellCrumbSetterContext)
  return (
    <header
      className={cn(
        "shrink-0 border-b border-black/5 dark:border-white/[0.07] bg-background",
        className
      )}
    >
      <div className="flex min-h-[52px] items-center gap-2 px-4 py-[9px] sm:px-5">
        {/* Mobile: the rail is a sheet, so the bar carries its trigger */}
        <SidebarTrigger className="-ml-1 md:hidden text-foreground/60" />
        <ShellBreadcrumb />
        <div ref={setCrumbSlot ?? undefined} className="flex min-w-0 items-center gap-2 empty:hidden" />
        <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
          {children}
          <div ref={setSlot ?? undefined} className="flex items-center gap-2 empty:hidden" />
        </div>
      </div>
      {below}
    </header>
  )
}
